/* eslint-disable react/no-unescaped-entities */
import { Button, Divider, Flex, Loader, Paper, Text, ThemeIcon } from "@mantine/core";
import { Download, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useListExports } from "../../../hooks/QueryHooks";
import { ListExportsResponse } from "../../../services/exports/exportsEndpoints.interface";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import ExportsList from "./components/ExportsList";

const ExportsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { data, isLoading, refetch } = useListExports({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });
  
  // Safely cast the data to the expected type
  const exports = data as ListExportsResponse | undefined;
  const hasExports = exports?.items && exports.items.length > 0;

  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch().then(() => {
      setTimeout(() => setIsRefreshing(false), 500); // Give visual feedback
    });
  };

  return (
    <div className="overflow-auto relative min-h-full h-[calc(100vh-90px)] bg-gray-100 p-4">
      <div className="max-w-4xl w-full min-h-full mx-auto flex flex-col">
        {/* Main content */}
        <Paper shadow="sm" p="lg" radius="md" className="bg-white flex-1" withBorder>
          {isLoading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <Loader size="md" />
              <Text c="dimmed" size="sm" mt="md">Loading your exports...</Text>
            </div>
          ) : !hasExports ? (
            <div className="flex flex-col justify-center items-center py-20">
              <ThemeIcon size={80} radius={100} color="gray" variant="light">
                <Download size={40} opacity={0.8} />
              </ThemeIcon>
              <Text fw={500} size="lg" mt="md">No exports yet</Text>
              <Text c="dimmed" size="sm" mt={4} ta="center" className="max-w-md">
                You haven&apos;t created any exports yet. Create your first export to download your data.  
              </Text>
            </div>
          ) : (
            <>
            <Flex justify="space-between" align="center" className="mb-4" s>
              <Text size="md" fw={500}>Export History</Text>
              <Button 
                variant="light" 
                leftSection={<RefreshCw size={16} />} 
                size="sm"
                onClick={handleRefresh}
                loading={isRefreshing}
              >
                Refresh
              </Button>
            </Flex>
              <Divider className="mb-4" />
              <ExportsList exports={exports?.items || []} />
            </>
          )}
        </Paper>
        
        {/* Help text */}
        {hasExports && (
          <Text c="dimmed" size="xs" mt="sm" ta="center">
              Need help with your exports? <a href="#" className="text-blue-500 hover:underline">View documentation</a>
          </Text>
        )}
      </div>
    </div>
  );
};

export default ExportsPage;
